import { useState, useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBalance } from '../contexts/BalanceContext';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from '../i18n/I18nContext';
import { apiClient } from '../utils/api-client';
import ConfirmDialog from './ConfirmDialog';
import SettingsDialog from '../features/settings/SettingsDialog';
import FeedbackDialog from '../features/feedback/FeedbackDialog';
import AddFundsDialog from '../features/billing/AddFundsDialog';
import { IconButton } from './ui/icon-button';
import { Badge } from './ui/badge';

interface LayoutProps {
  children: ReactNode;
  title?: string;
}

const Layout: FC<LayoutProps> = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { balance, refreshBalance } = useBalance();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showAddFunds, setShowAddFunds] = useState(false);

  // Refresh balance on route change
  useEffect(() => {
    refreshBalance();
  }, [refreshBalance, location.pathname]);

  // Handle Stripe checkout return — poll balance until webhook has credited it
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      showToast(t('funds.payment_success'), 'success');
      window.history.replaceState({}, document.title, window.location.pathname);
      const priorBalance = balance?.effectiveBalance ?? 0;
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const data = await apiClient.get<{ effectiveBalance: number }>('/accounts/user/balance');
        if (data.effectiveBalance !== priorBalance || attempts >= 10) {
          refreshBalance();
        } else {
          setTimeout(poll, 1000);
        }
      };
      poll();
    }
    if (searchParams.get('canceled') === 'true') {
      showToast(t('funds.payment_canceled'), 'warning');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams, showToast]);

  // Determine header button states based on current route
  const pathname = location.pathname;
  const isArticleList = pathname === '/articles';
  const isVocab = pathname === '/vocab';

  const navToVocab = () => {
    if (!isVocab) {
      navigate('/vocab', { state: { from: pathname } });
    }
  };

  return (
    <div className="h-dvh flex flex-col">
      {/* Slim header */}
      <nav className="shrink-0 border-b border-border bg-background">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/articles" className="flex items-center gap-2 text-lg font-bold text-foreground shrink-0">
              <img src="/icon-192.png" alt="" className="h-8 w-8 rounded-lg" />
              <span className="hidden sm:inline">{import.meta.env.VITE_BRAND_NAME}</span>
            </Link>
            <div className="flex items-center gap-1.5">
              {user && balance && (
                <button onClick={() => setShowAddFunds(true)}>
                  <Badge variant={balance.effectiveBalance < 10 ? 'destructive' : balance.effectiveBalance < 20 ? 'warning' : 'info'} className="text-sm">
                    <span className="hidden sm:inline">{t('nav.balance')}&nbsp;</span>{balance.effectiveBalanceFormatted}
                  </Badge>
                </button>
              )}
              {user && (
                <>
                  <span className="hidden md:inline text-xs text-muted-foreground truncate max-w-[180px] px-2">{user.email}</span>
                  <IconButton onClick={() => navigate('/articles')} title={t('nav.articles')} disabled={isArticleList}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  </IconButton>
                  <IconButton onClick={navToVocab} title={t('nav.vocab')} disabled={isVocab}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
                      <path d="M8 7h6" /><path d="M8 11h4" />
                    </svg>
                  </IconButton>
                  <IconButton onClick={() => setShowSettings(true)} title={t('settings.title')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </IconButton>
                  <IconButton onClick={() => setShowFeedback(true)} title={t('nav.feedback')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </IconButton>
                  <IconButton onClick={() => setShowLogoutConfirm(true)} title={t('nav.logout')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </IconButton>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="grow overflow-auto overscroll-contain">
        <div className="max-w-5xl mx-auto px-4 py-4">
          {title && <h1 className="text-2xl font-semibold mb-4">{title}</h1>}
          {children}
        </div>
      </main>

      {/* Dialogs */}
      <ConfirmDialog
        open={showLogoutConfirm}
        title={t('nav.logout_title')}
        message={t('nav.logout_message')}
        confirmLabel={t('nav.logout_title')}
        destructive
        onConfirm={() => {
          setShowLogoutConfirm(false);
          logout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      <SettingsDialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
      />

      <FeedbackDialog
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
      />

      <AddFundsDialog
        open={showAddFunds}
        onClose={() => setShowAddFunds(false)}
      />
    </div>
  );
};

export default Layout;
